import type { ReactNode } from "react";
import { cn } from "@/shared/lib/cn";
import { useAway } from "@/shared/presence/presence.context";

/**
 * The away marker, and the only copy of it.
 *
 * ## Why it is its own component
 *
 * This app has TWO shared avatars: `UserAvatar` (eighteen files, a size
 * scale, `shared/lib/initials`) and `PersonAvatar` (seven files, a person
 * object, `shared/components/person/initials`). They predate this work and
 * consolidating them is a separate job with a much larger diff.
 *
 * What could not wait is presence appearing on only one of them. A badge that
 * shows up on the leave tables and not the directory is worse than no badge:
 * a reader who has learned that a dot means "away" will read its absence as
 * "here", and on half the app that would be wrong. So the marker lives here
 * and both avatars render it, which also means the day the two components
 * merge, this one does not have to.
 *
 * ## Why it WRAPS instead of being dropped in beside the avatar
 *
 * A positioned dot needs a positioned parent, and the obvious move (always
 * wrap the avatar in a `relative` span) quietly breaks any caller that
 * positions the avatar itself through `className`. There is one such caller
 * today: the apply-for-leave form floats the manager's avatar inside a text
 * input with `absolute top-1/2 left-2`, and an unconditional wrapper
 * re-anchors that to the wrapper instead of the field.
 *
 * So when nobody is away, this returns the avatar EXACTLY as it was given,
 * with no extra element in the tree. That is what makes the marker safe to
 * add to two components with twenty five call sites between them without
 * reading all of them.
 */
export function AwayMarker({
  membershipId,
  ringClassName,
  children,
}: {
  membershipId: string | undefined | null;
  /**
   * Must match the surface BEHIND the avatar, not the avatar itself.
   *
   * The dot sits on the avatar's edge over an image of unknown brightness,
   * and the ring is what separates the two. Pass `ring-card` on a card,
   * `ring-surface-subtle` on a tinted row. Defaults to the page background.
   */
  ringClassName?: string;
  children: ReactNode;
}): ReactNode {
  const away = useAway(membershipId);
  if (!away) return <>{children}</>;

  return (
    <span className="relative inline-flex h-fit shrink-0">
      {children}
      {/*
       * Sized as a fraction of the avatar, then CLAMPED at both ends, so one
       * component covers the 20px avatar in a chip row and the 64px one on a
       * profile with no size prop to keep in sync. The clamps are the design,
       * not defensiveness: a pure percentage puts a 20px blob on a profile
       * header, which stops reading as a marker ON an avatar, and shrinks
       * below 6px in a chip row, where it vanishes.
       *
       * The inset is the difference between the avatar's BOX and the circle
       * drawn inside it. `bottom-0 right-0` is the square's corner, and a
       * circle's edge at 45 degrees is about 15% of the width in from there.
       * At 32px the dot is large enough to straddle that gap anyway; at 64px
       * it detaches and floats beside the avatar instead of sitting on it.
       *
       * `aria-hidden`: the dot is decorative. The fact reaches a screen
       * reader as text every place this is used, from the row's second line,
       * the hover card or the profile banner. Announcing it here too would
       * read the absence twice.
       */}
      <span
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute right-[7%] bottom-[7%] block size-[32%] max-h-3.5 max-w-3.5 min-h-1.5 min-w-1.5 rounded-full bg-warning ring-2",
          ringClassName ?? "ring-surface-base",
        )}
      />
    </span>
  );
}
